import { AlertCircle, ArrowRight } from "lucide-react";

/**
 * Dark hero strip surfacing items that need the operator's attention.
 *
 * Only renders if at least one signal is non-zero. Each signal becomes a
 * clickable shortcut that routes back into the relevant tab.
 *
 * Props:
 *   stats           : DashboardStats from /api/admin/stats
 *   onJumpToTab(id) : callback , the dashboard owns the active tab state.
 */
export const AttentionStrip = ({ stats, onJumpToTab }) => {
  if (!stats) return null;

  const items = [];

  if (stats.pending_reviews > 0) {
    items.push({
      key: "reviews",
      count: stats.pending_reviews,
      label:
        stats.pending_reviews === 1
          ? "review to moderate"
          : "reviews to moderate",
      cta: "Open reviews",
      onClick: () => onJumpToTab?.("reviews"),
    });
  }

  if (stats.stale_interests > 0) {
    items.push({
      key: "stale-interests",
      count: stats.stale_interests,
      label:
        stats.stale_interests === 1
          ? "interest waiting >24h"
          : "interests waiting >24h",
      cta: "Open interests",
      onClick: () => onJumpToTab?.("interests"),
    });
  }

  if (stats.new_interests_7d > 0) {
    items.push({
      key: "new-interests",
      count: stats.new_interests_7d,
      label:
        stats.new_interests_7d === 1
          ? "new interest this week"
          : "new interests this week",
      cta: "Open interests",
      onClick: () => onJumpToTab?.("interests"),
    });
  }

  if (stats.studies_closing_soon > 0) {
    items.push({
      key: "closing-studies",
      count: stats.studies_closing_soon,
      label:
        stats.studies_closing_soon === 1
          ? "study closing soon"
          : "studies closing soon",
      cta: "Open studies",
      onClick: () => onJumpToTab?.("studies"),
    });
  }

  if (items.length === 0) return null;

  return (
    <div
      className="rounded-2xl bg-[var(--dark-bg)] text-white px-5 py-4 mb-6 shadow-md grain-dark relative overflow-hidden"
      data-testid="attention-strip"
    >
      {/* Left lime accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--lime)]" />
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--lime)]/15 text-[var(--lime)]">
            <AlertCircle size={18} strokeWidth={2.25} />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-0.5">
              Needs your attention
            </p>
            <p className="text-sm text-white/90 font-medium leading-snug">
              {items
                .map((it) => `${it.count} ${it.label}`)
                .join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-[var(--lime)] hover:text-[var(--dark-bg)] text-white text-[12px] font-semibold transition-colors"
              data-testid={`attention-cta-${it.key}`}
            >
              {it.cta}
              <ArrowRight size={12} strokeWidth={2.5} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttentionStrip;
